import React, { useState, useEffect, useRef } from "react";
import "./LoginModel.css";
import leftFood from "../../Assets/images/leftFood.webp";
import { useLoginModal } from "../../App/LoginModalProvider";
import {
  generateOtp,
  toastError,
  toastSuccess,
  validateOtp,
  registerUser,
} from "../../api/services";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setTokens, setUser } from "../../Features/User/userSlice";

const LoginModel = () => {
  const { isLoginOpen, closeLoginModal } = useLoginModal();
  const OTP_LENGTH = 4;
  const dispatch = useDispatch();

  const [mode, setMode] = useState("mobile");
  const [fadeOut, setFadeOut] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [forgotOtp, setForgotOtp] = useState(Array(OTP_LENGTH).fill(""));

  const [registerData, setRegisterData] = useState({
    firstName: "",
    email: "",
    mobile: "",
    gender: "",
    pin: "",
  });

  // Refs to prevent double API calls
  const isRequestInProgress = useRef(false);

  const inputRefs = useRef([]);
  const inputForgotRefs = useRef([]);
  const inputRegisterRefs = useRef([]);

  useEffect(() => {
    if (isLoginOpen) {
      document.body.style.overflow = "hidden";
      setShowPopup(true);
      setFadeOut(false);
      resetForm();
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isLoginOpen]);

  const resetForm = () => {
    setMode("mobile");
    setMobileNumber("");
    setTermsAccepted(false);
    setOtp(Array(OTP_LENGTH).fill(""));
    setForgotOtp(Array(OTP_LENGTH).fill(""));
    setError("");
    setRegisterData({
      firstName: "",
      email: "",
      mobile: "",
      gender: "",
      pin: "",
    });
    isRequestInProgress.current = false;
  };

  const isMobileValid =
    mobileNumber.length === 10 && /^\d{10}$/.test(mobileNumber);
  const isOtpComplete = otp.every((digit) => digit !== "");
  const isForgotOtpComplete = forgotOtp.every((digit) => digit !== "");
  const isContinueDisabled = !isMobileValid || !termsAccepted;

  const handleClose = () => {
    setFadeOut(true);
    setTimeout(() => {
      setShowPopup(false);
      closeLoginModal();
    }, 300);
  };

  const handleContinue = async () => {
    // Prevent double call
    if (isRequestInProgress.current || isLoading) return;

    if (!isMobileValid) {
      setError("Please enter a valid 10-digit mobile number.");
      toastError("Invalid mobile number");
      return;
    }
    if (!termsAccepted) {
      setError("Please accept the Terms & Conditions.");
      toastError("Please accept Terms & Conditions");
      return;
    }
    setError("");
    setIsLoading(true);
    isRequestInProgress.current = true;

    try {
      const response = await generateOtp({ mobile: mobileNumber });
      if (response?.success || response?.status === 200) {
        toastSuccess("OTP sent successfully 🚀");
        setMode("otp");
        // Clear OTP inputs when entering OTP mode
        setOtp(Array(OTP_LENGTH).fill(""));
        // Focus first OTP input after mode change
        setTimeout(() => inputRefs.current[0]?.focus(), 50);
      } else {
        toastError(response?.message || "Failed to send OTP");
        setError(response?.message || "Failed to send OTP");
      }
    } catch (err) {
      console.error(err);
      toastError("Failed to send OTP. Please try again.");
      setError("Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
      isRequestInProgress.current = false;
    }
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Paste support for OTP
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").slice(0, OTP_LENGTH);
    if (/^\d+$/.test(paste)) {
      const newOtp = paste
        .split("")
        .concat(Array(OTP_LENGTH).fill(""))
        .slice(0, OTP_LENGTH);
      setOtp(newOtp);
      // Focus last filled or next empty
      const lastFilledIndex = newOtp.findLastIndex((d) => d !== "");
      const focusIndex =
        lastFilledIndex === OTP_LENGTH - 1
          ? lastFilledIndex
          : lastFilledIndex + 1;
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleLogin = async () => {
    if (isRequestInProgress.current || isLoading) return;

    const finalOtp = otp.join("");
    if (finalOtp.length !== OTP_LENGTH) {
      setError("Please enter complete OTP");
      return;
    }
    setIsLoading(true);
    isRequestInProgress.current = true;
    try {
      const response = await validateOtp({
        mobile: mobileNumber,
        otp: finalOtp,
      });
      if (response?.success || response?.status === 200) {
        const TokenPayload = {
          accessToken: response?.data?.token,
          refreshToken: response?.data?.refreshToken,
        };
        dispatch(setTokens(TokenPayload));
        dispatch(setUser(response?.data?.user));
        handleClose();
        toastSuccess("Login successful!");
      } else {
        toastError(response?.message || "Invalid OTP");
        setError("Invalid OTP. Please try again.");
      }
    } catch (err) {
      console.error(err);
      toastError("Login failed. Please try again.");
      setError("Invalid OTP. Please try again.");
    } finally {
      setIsLoading(false);
      isRequestInProgress.current = false;
    }
  };

  const handleForgotPasswordClick = () => {
    setMode("forgot");
    setError("");
    setForgotOtp(Array(OTP_LENGTH).fill(""));
    toastSuccess("OTP sent to your mobile number");
    setTimeout(() => inputForgotRefs.current[0]?.focus(), 50);
  };

  const handleForgotOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...forgotOtp];
    newOtp[index] = value;
    setForgotOtp(newOtp);
    if (value && index < OTP_LENGTH - 1) {
      inputForgotRefs.current[index + 1]?.focus();
    }
  };

  const handleForgotOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !forgotOtp[index] && index > 0) {
      inputForgotRefs.current[index - 1]?.focus();
    }
  };

  const handleForgotPassword = async () => {
    if (isRequestInProgress.current || isLoading) return;

    const finalOtp = forgotOtp.join("");
    if (finalOtp.length !== OTP_LENGTH) {
      setError("Please enter complete OTP");
      return;
    }
    setIsLoading(true);
    isRequestInProgress.current = true;
    try {
      // Replace with your actual reset password API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toastSuccess("Password reset link sent to your email");
      setMode("mobile");
      setError("");
    } catch (err) {
      setError("Invalid OTP. Please try again.");
      toastError("OTP verification failed");
    } finally {
      setIsLoading(false);
      isRequestInProgress.current = false;
    }
  };

  const handleRegisterClick = () => {
    setMode("register");
    setError("");
    // Reset pin when entering registration
    setRegisterData((prev) => ({ ...prev, pin: "" }));
    setTimeout(() => inputRegisterRefs.current[0]?.focus(), 50);
  };

  const handleRegisterInputChange = (e) => {
    const { name, value } = e.target;
    setRegisterData((prev) => ({ ...prev, [name]: value }));
  };

  // Fixed PIN input handling
  const handleRegisterOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    let newPin = registerData.pin.split("");
    newPin[index] = value;
    newPin = newPin.join("").slice(0, OTP_LENGTH);
    setRegisterData((prev) => ({ ...prev, pin: newPin }));
    if (value && index < OTP_LENGTH - 1) {
      inputRegisterRefs.current[index + 1]?.focus();
    }
  };

  const handleRegisterOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !registerData.pin[index] && index > 0) {
      inputRegisterRefs.current[index - 1]?.focus();
    }
  };

  const handleRegisterPaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").slice(0, OTP_LENGTH);
    if (/^\d+$/.test(paste)) {
      setRegisterData((prev) => ({ ...prev, pin: paste }));
      const lastIndex = paste.length - 1;
      inputRegisterRefs.current[lastIndex]?.focus();
    }
  };

  const validateRegisterForm = () => {
    if (!registerData.firstName.trim()) {
      setError("Full name is required");
      return false;
    }
    if (
      !registerData.email.trim() ||
      !/\S+@\S+\.\S+/.test(registerData.email)
    ) {
      setError("Valid email is required");
      return false;
    }
    if (!registerData.mobile || !/^\d{10}$/.test(registerData.mobile)) {
      setError("Valid 10-digit mobile number is required");
      return false;
    }
    if (!registerData.gender) {
      setError("Please select gender");
      return false;
    }
    if (!registerData.pin || registerData.pin.length !== OTP_LENGTH) {
      setError(`PIN must be ${OTP_LENGTH} digits`);
      return false;
    }
    if (!termsAccepted) {
      setError("Please accept Terms & Conditions");
      return false;
    }
    return true;
  };

  const handleRegisterSubmit = async () => {
    if (isRequestInProgress.current) return;

    isRequestInProgress.current = true; // 👈 move UP

    if (!validateRegisterForm()) {
      isRequestInProgress.current = false;
      return;
    }

    setIsLoading(true);

    try {
      const payload = { ...registerData };

      const response = await registerUser(payload);

      if (response?.data?.success) {
        const TokenPayload = {
          accessToken: response?.data?.data?.token,
          refreshToken: response?.data?.data?.refreshToken,
        };
        dispatch(setTokens(TokenPayload));
        dispatch(setUser(response?.data?.data?.user));
        handleClose();
        toastSuccess("Registration successful!");
        // Reset form after success
        setMode("mobile");
        setMobileNumber(registerData.mobile);
        setRegisterData({
          firstName: "",
          email: "",
          mobile: "",
          gender: "",
          pin: "",
        });
        setTermsAccepted(false);
        setError("");
      } else {
        toastError(response?.message || "Registration failed");
        setError(response?.message || "Registration failed");
      }
    } catch (err) {
      console.error(err);
      toastError("Registration failed. Please try again.");
      setError("Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
      isRequestInProgress.current = false;
    }
  };

  const goBackToMobile = () => {
    setMode("mobile");
    setOtp(Array(OTP_LENGTH).fill(""));
    setError("");
  };

  const goBackToLogin = () => {
    setMode("mobile");
    setError("");
  };

  if (!showPopup) return null;

  return (
    <div
      className={`popup-overlay ${fadeOut ? "fade-out" : ""}`}
      onClick={handleClose}
    >
      <div className="containerLogin" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header border-0">
          <button type="button" className="close" onClick={handleClose}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="left-panel">
          <img
            src={leftFood}
            alt="Skill India Logo"
            className="hero left-panel-width"
          />
        </div>
        <div className="right-panel">
          {mode === "mobile" && (
            <div className="register-phone-detail">
              <div className="form-group">
                <label htmlFor="mobileNumber">Mobile Number</label>
                <div className="phone-input">
                  <span>+91</span>
                  <input
                    type="tel"
                    id="mobileNumber"
                    maxLength={10}
                    placeholder="Enter 10-digit mobile number"
                    value={mobileNumber}
                    onChange={(e) =>
                      setMobileNumber(e.target.value.replace(/\D/g, ""))
                    }
                    autoFocus
                  />
                </div>
                {error && <div className="error-text">{error}</div>}
              </div>
              <div className="form-footer">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="termsCheckbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                  />
                  <label htmlFor="termsCheckbox">
                    I agree to accept all applicable{" "}
                    <Link to="#" onClick={(e) => e.preventDefault()}>
                      Terms & Conditions
                    </Link>{" "}
                    and{" "}
                    <Link to="#" onClick={(e) => e.preventDefault()}>
                      Privacy Policy
                    </Link>
                  </label>
                </div>
                <button
                  className={
                    isContinueDisabled
                      ? "continue-button"
                      : "continue-button-submit"
                  }
                  onClick={handleContinue}
                  disabled={isContinueDisabled || isLoading}
                >
                  {isLoading ? <span className="spinner"></span> : "CONTINUE"}
                </button>
                <div className="change-number">
                  New user?{" "}
                  <Link to="#" onClick={handleRegisterClick}>
                    Register here
                  </Link>
                </div>
              </div>
            </div>
          )}

          {mode === "otp" && (
            <div className="password-container">
              <div className="back-button" onClick={goBackToMobile}>
                ⬅ Back
              </div>
              <div className="password-content">
                <h3 className="password-title">Enter OTP</h3>
                <p className="password-subtitle">
                  Enter the 4-digit OTP sent to {mobileNumber}
                </p>
                {error && (
                  <div className="error-text" style={{ textAlign: "center" }}>
                    {error}
                  </div>
                )}
                <div className="otp-box" onPaste={handleOtpPaste}>
                  {otp.map((digit, idx) => (
                    <input
                      key={idx}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(e.target.value, idx)}
                      onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                      ref={(el) => (inputRefs.current[idx] = el)}
                      className="otp-input"
                      autoFocus={idx === 0}
                    />
                  ))}
                </div>
              </div>
              <div className="password-footer">
                <button
                  className="forgot-button"
                  onClick={handleForgotPasswordClick}
                >
                  FORGOT PASSWORD?
                </button>
                <button
                  className={isOtpComplete ? "login-active" : "login-disabled"}
                  onClick={handleLogin}
                  disabled={!isOtpComplete || isLoading}
                >
                  {isLoading ? <span className="spinner"></span> : "LOGIN"}
                </button>
              </div>
            </div>
          )}

          {mode === "forgot" && (
            <div className="password-container">
              <div className="back-button" onClick={goBackToMobile}>
                ⬅ Back
              </div>
              <div className="password-content">
                <h3 className="password-title">Reset Password</h3>
                <p className="password-subtitle">
                  Enter the 4-digit OTP sent to your mobile
                </p>
                {error && (
                  <div className="error-text" style={{ textAlign: "center" }}>
                    {error}
                  </div>
                )}
                <div className="otp-box">
                  {forgotOtp.map((digit, idx) => (
                    <input
                      key={idx}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) =>
                        handleForgotOtpChange(e.target.value, idx)
                      }
                      onKeyDown={(e) => handleForgotOtpKeyDown(e, idx)}
                      ref={(el) => (inputForgotRefs.current[idx] = el)}
                      className="otp-input"
                      autoFocus={idx === 0}
                    />
                  ))}
                </div>
              </div>
              <div className="password-footer">
                <button
                  className={
                    isForgotOtpComplete ? "login-active" : "login-disabled"
                  }
                  onClick={handleForgotPassword}
                  disabled={!isForgotOtpComplete || isLoading}
                >
                  {isLoading ? (
                    <span className="spinner"></span>
                  ) : (
                    "RESET PASSWORD"
                  )}
                </button>
              </div>
            </div>
          )}

          {mode === "register" && (
            <div className="register-container">
              <div className="back-button" onClick={goBackToLogin}>
                ⬅ Back to Login
              </div>
              <div className="register-form">
                <h3 className="register-title">Create Account</h3>
                <p className="register-subtitle">
                  Please fill in your details to register
                </p>
                {error && <div className="error-text">{error}</div>}
                <div className="row">
                  <div className="col-12 col-md-6 col-lg-6">
                    <div className="form-group">
                      <label>Full Name</label>
                      <div className="text-input">
                        <input
                          type="text"
                          name="firstName"
                          placeholder="Enter your full name"
                          value={registerData.firstName}
                          onChange={handleRegisterInputChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-6">
                    <div className="form-group">
                      <label>Email Address</label>
                      <div className="text-input">
                        <input
                          type="email"
                          name="email"
                          placeholder="Enter your email"
                          value={registerData.email}
                          onChange={handleRegisterInputChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 col-md-6 col-lg-6">
                    <div className="form-group">
                      <label>Mobile Number</label>
                      <div className="phone-input">
                        <span>+91</span>
                        <input
                          type="tel"
                          name="mobile"
                          maxLength={10}
                          placeholder="Enter 10-digit mobile number"
                          value={registerData.mobile}
                          onChange={handleRegisterInputChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-6">
                    <div className="form-group">
                      <label>Gender</label>
                      <select
                        name="gender"
                        value={registerData.gender}
                        className="text-input"
                        onChange={handleRegisterInputChange}
                        style={{ padding: "12px", width: "100%" }}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="password-content">
                  <p
                    className="password-subtitle"
                    style={{ marginBottom: "4px" }}
                  >
                    Enter the 4-digit PIN
                  </p>
                  <div className="otp-box" onPaste={handleRegisterPaste}>
                    {Array(OTP_LENGTH)
                      .fill(0)
                      .map((_, idx) => (
                        <input
                          key={idx}
                          type="text"
                          maxLength="1"
                          value={registerData.pin[idx] || ""}
                          onChange={(e) =>
                            handleRegisterOtpChange(e.target.value, idx)
                          }
                          onKeyDown={(e) => handleRegisterOtpKeyDown(e, idx)}
                          ref={(el) => (inputRegisterRefs.current[idx] = el)}
                          className="otp-input"
                          autoFocus={idx === 0}
                        />
                      ))}
                  </div>
                </div>

                <div className="checkbox-group checkbox-group-dd">
                  <input
                    type="checkbox"
                    id="registerTerms"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                  />
                  <label htmlFor="registerTerms">
                    I agree to all applicable{" "}
                    <Link to="#" onClick={(e) => e.preventDefault()}>
                      Terms & Conditions
                    </Link>{" "}
                    and{" "}
                    <Link to="#" onClick={(e) => e.preventDefault()}>
                      Privacy Policy
                    </Link>
                  </label>
                </div>

                <button
                  className="continue-button-submit"
                  onClick={handleRegisterSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? <span className="spinner"></span> : "REGISTER"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModel;
